import { useMemo, useState } from 'react';
import { Archive, AlertTriangle, Trash2 } from 'lucide-react';
import { Modal } from './Modal';
import type { Tournament } from '../models';
import {
  archiveTournament,
  canArchiveTournament,
  canDeleteTournament,
  deleteTournament,
  getTournamentDeletionImpact,
  type TournamentDeletionImpact,
} from '../services/tournamentService';

type CompetitionAction = 'delete' | 'archive';

interface ActionConfig {
  action: CompetitionAction;
  label: string;
  title: string;
  tone: 'danger' | 'warn';
}

interface CompetitionLifecycleActionProps {
  tournament: Tournament;
  onComplete?: (action: CompetitionAction) => void;
  variant?: 'button' | 'zone';
  size?: 'sm' | 'md';
}

export function CompetitionLifecycleAction({
  tournament,
  onComplete,
  variant = 'button',
  size = 'sm',
}: CompetitionLifecycleActionProps) {
  const [open, setOpen] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [error, setError] = useState('');
  const config = actionConfig(tournament);
  const impact = useMemo(() => getTournamentDeletionImpact(tournament.id), [tournament.id]);

  if (!config) {
    if (variant === 'zone' && tournament.status === 'archived') {
      return (
        <div className="card" style={{ borderColor: 'var(--surface-line-strong)' }}>
          <div className="card__head">
            <div>
              <h3 className="card__title">Archived</h3>
              <p className="card__sub">This competition is already hidden from default league views.</p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const noun = (tournament.format ?? 'tournament') === 'season' ? 'season' : 'tournament';
  const enabled = typedName === tournament.name;

  const onConfirm = () => {
    setError('');
    try {
      if (config.action === 'delete') {
        deleteTournament(tournament.id);
      } else {
        archiveTournament(tournament.id);
      }
      setOpen(false);
      setTypedName('');
      onComplete?.(config.action);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not complete this action.');
    }
  };

  const button = (
    <button
      type="button"
      className={`btn ${config.tone === 'danger' ? 'btn--danger' : 'btn--ghost'} ${size === 'sm' ? 'btn--sm' : ''}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setError('');
        setTypedName('');
        setOpen(true);
      }}
    >
      {config.action === 'delete' ? <Trash2 size={14} /> : <Archive size={14} />}
      {config.label}
    </button>
  );

  return (
    <>
      {variant === 'zone' ? (
        <div className="card" style={{ borderColor: config.tone === 'danger' ? 'var(--err)' : 'var(--warn)' }}>
          <div className="card__head">
            <div>
              <h3 className="card__title">
                <AlertTriangle
                  size={16}
                  style={{
                    verticalAlign: -2,
                    marginRight: 6,
                    color: config.tone === 'danger' ? 'var(--err)' : 'var(--warn)',
                  }}
                />
                Danger zone
              </h3>
              <p className="card__sub">
                {config.action === 'delete'
                  ? `Delete this draft ${noun} and its setup data.`
                  : `Archive this ${noun} so it no longer appears in default league views.`}
              </p>
            </div>
            {button}
          </div>
        </div>
      ) : (
        button
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={config.title}
        subtitle={`Type "${tournament.name}" to confirm.`}
        footer={
          <>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn ${config.tone === 'danger' ? 'btn--danger' : 'btn--ghost'} btn--sm`}
              style={config.tone === 'warn' ? { borderColor: 'var(--warn)' } : undefined}
              disabled={!enabled}
              onClick={onConfirm}
            >
              {config.action === 'delete' ? <Trash2 size={13} /> : <Archive size={13} />}
              {config.label}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: 14, marginTop: 16 }}>
          <CompetitionSummary tournament={tournament} action={config.action} />

          <div className="note note--warn">
            {config.action === 'delete'
              ? `This permanently removes the ${noun} and tournament-scoped setup records from this browser's league data.`
              : `This preserves history but hides the ${noun} from default League HQ and public event views.`}
          </div>

          <ImpactList action={config.action} impact={impact} />

          <label className="field">
            <span className="field__label">Confirm name</span>
            <input
              className="input"
              value={typedName}
              onChange={(event) => setTypedName(event.target.value)}
              placeholder={tournament.name}
              autoComplete="off"
            />
          </label>
          {error && <p style={{ color: 'var(--err)', fontSize: 13, margin: 0 }}>{error}</p>}
        </div>
      </Modal>
    </>
  );
}

function actionConfig(tournament: Tournament): ActionConfig | null {
  if (canDeleteTournament(tournament)) {
    return {
      action: 'delete',
      label: 'Delete',
      title: `Delete ${competitionLabel(tournament)}?`,
      tone: 'danger',
    };
  }
  if (canArchiveTournament(tournament)) {
    return {
      action: 'archive',
      label: 'Archive',
      title: `Archive ${competitionLabel(tournament)}?`,
      tone: 'warn',
    };
  }
  return null;
}

function competitionLabel(tournament: Tournament) {
  const noun = (tournament.format ?? 'tournament') === 'season' ? 'season' : 'tournament';
  return `${noun} "${tournament.name}"`;
}

function CompetitionSummary({
  tournament,
  action,
}: {
  tournament: Tournament;
  action: CompetitionAction;
}) {
  return (
    <div className="grid grid--2" style={{ gap: 10 }}>
      <ImpactItem label="Competition" value={tournament.name} />
      <ImpactItem label="Format" value={(tournament.format ?? 'tournament') === 'season' ? 'Season' : 'Tournament'} />
      <ImpactItem label="Current status" value={formatStatus(tournament.status)} />
      <ImpactItem label="Action" value={action === 'delete' ? 'Permanent delete' : 'Archive'} />
    </div>
  );
}

function ImpactList({
  action,
  impact,
}: {
  action: CompetitionAction;
  impact: TournamentDeletionImpact;
}) {
  if (action === 'archive') {
    return (
      <div className="grid grid--2" style={{ gap: 10 }}>
        <ImpactItem label="Status" value="Moves to archived" />
        <ImpactItem label="Records" value="Preserved" />
      </div>
    );
  }

  const items = [
    ['Divisions', impact.divisions],
    ['Teams', impact.teams],
    ['Roster entries', impact.rosterEntries],
    ['Games', impact.games],
    ['Schedule slots', impact.scheduleSlots],
    ['Scores', impact.scoreEvents + impact.gameResults],
    ['Standings', impact.standings],
    ['Assignments', impact.gameAssignments + impact.officialAssignments + impact.roleAssignments],
    ['Registrations', impact.registrations],
    ['Payments', impact.paymentIntents],
    ['Communications', impact.communications + impact.announcements + impact.lockerRoomMessages],
    ['Waivers', impact.waiverDocuments + impact.waivers],
    ['Eligibility records', impact.playerEligibility],
  ].filter(([, value]) => Number(value) > 0);

  if (items.length === 0) {
    return <p className="muted" style={{ margin: 0 }}>No linked setup records found.</p>;
  }

  return (
    <div className="grid grid--2" style={{ gap: 10 }}>
      {items.map(([label, value]) => (
        <ImpactItem key={String(label)} label={String(label)} value={String(value)} />
      ))}
    </div>
  );
}

function ImpactItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ border: '1px solid var(--surface-line)', borderRadius: 8, padding: 10 }}>
      <div className="stat__label">{label}</div>
      <div style={{ fontWeight: 800, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function formatStatus(status: Tournament['status']): string {
  return status.replace('_', ' ');
}
